import React from 'react';
import { ChevronLeft, Loader2, Save, Eye, type LucideIcon } from 'lucide-react';

// Shared sticky chrome for the full-page builders (featured event / video). Dark page,
// beige cards below — matches the target look for all four builders (the course
// builder gets flipped to this in Phase 3). Mirrors CourseBuilderPage's bar +
// dirty-glow behavior.
interface BuilderTopBarProps {
  kicker: string;
  kickerIcon: LucideIcon;
  kickerColor: string; // full-strength accent for the icon
  title: string;
  titlePlaceholder: string;
  meta?: string;
  onBack: () => void;
  saving: boolean;
  dirty: boolean;
  saveLabel: string;
  onSave: () => void;
  previewHref?: string; // renders a Preview ghost link when set (saved products)
}

export default function BuilderTopBar({
  kicker, kickerIcon: KickerIcon, kickerColor, title, titlePlaceholder, meta,
  onBack, saving, dirty, saveLabel, onSave, previewHref,
}: BuilderTopBarProps) {
  const ghost: React.CSSProperties = {
    padding: '8px 14px',
    borderRadius: 999,
    background: 'transparent',
    border: '1px solid var(--hj-hairline)',
    color: 'var(--hj-ink-2)',
    fontSize: 12.5,
    fontWeight: 500,
    fontFamily: 'inherit',
    textDecoration: 'none',
    cursor: 'pointer',
    minHeight: 38,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  return (
    <div
      className="sticky top-0 z-10"
      style={{ background: 'var(--hj-bg)', borderBottom: '1px solid var(--hj-hairline-soft)' }}
    >
      <div className="mx-auto flex items-center justify-between gap-3" style={{ maxWidth: 1120, padding: '14px 16px' }}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <button
            type="button"
            onClick={onBack}
            aria-label="Back to My Store"
            className="flex-shrink-0 inline-flex items-center justify-center"
            style={{ width: 36, height: 36, borderRadius: 999, background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', cursor: 'pointer' }}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)' }}>
              <KickerIcon className="w-3.5 h-3.5" style={{ color: kickerColor }} /> {kicker}
            </div>
            <h1
              className="font-hj-display m-0 truncate"
              style={{ fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700, marginTop: 2 }}
            >
              {title.trim() || titlePlaceholder}
            </h1>
            {meta && (
              <p className="m-0 tabular" style={{ marginTop: 2, fontSize: 11.5, color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>
                {meta}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {previewHref && (
            <a href={previewHref} target="_blank" rel="noopener noreferrer" style={ghost}>
              <Eye className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Preview</span>
            </a>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className={`inline-flex items-center gap-1.5 disabled:cursor-not-allowed ${dirty && !saving ? 'animate-pulse' : ''}`}
            style={{
              padding: '9px 18px',
              borderRadius: 999,
              background: 'var(--hj-blue)',
              border: dirty ? '2px solid oklch(0.45 0.15 80)' : '1px solid var(--hj-blue)',
              color: 'oklch(0.18 0.005 70)',
              fontSize: 13,
              fontWeight: 700,
              fontFamily: 'inherit',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.6 : 1,
              minHeight: 40,
              boxShadow: dirty && !saving ? '0 0 0 3px rgb(255 214 0 / 0.35)' : 'none',
            }}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{saveLabel}</span>
            <span className="sm:hidden">Save</span>
          </button>
        </div>
      </div>
    </div>
  );
}
